import type { NativeQuote } from "@/modules/dashboard/dispersions/services/paymentsQuotes.service";
import { compareDecimalStrings } from "@/shared/utils/decimal";

import type { BrebQrResolution } from "../types/brebQr.types";

/**
 * Whether the quote delivers exactly `amount` to the beneficiary. The fee inversion can overshoot at a fee
 * tier boundary, and a dynamic QR rejects any amount but its own, so "close" is not good enough.
 */
export function quotePaysExactly(quote: NativeQuote, amount: string): boolean {
  if (quote.direction !== "FIXED_RECEIVE" || !quote.amounts.payout_net) return false;
  return compareDecimalStrings(quote.amounts.payout_net, amount) === 0;
}

/** Mirrors the backend's unmasking rule: a merchant key, or a company (NIT) behind any key. */
export function isBusinessPayee(resolution: Pick<BrebQrResolution, "key_type" | "id_type">): boolean {
  return resolution.key_type === "MERCHANT" || resolution.id_type === "NIT";
}

/** A business is shown as it presents itself; a person's key keeps only what confirms it is the right one. */
export function maskBrebKeyForDisplay(keyType: string, keyValue: string): string {
  const value = keyValue.trim();
  if (!value) return "—";
  if (keyType === "EMAIL") {
    const [local, domain] = value.split("@");
    return domain ? `${local.slice(0, 2)}***@${domain}` : value;
  }
  if (keyType === "PHONE") {
    const digits = value.replace(/\D/g, "");
    return digits.length >= 10 ? `(${digits.slice(0, 3)}) ***-${digits.slice(-4)}` : value;
  }
  if (keyType === "NRIC") return value.length > 4 ? `****${value.slice(-4)}` : value;
  return value;
}
